import {
  Reservation,
  ReservationFieldErrors,
  ReservationFormState,
  ReservationRequest,
} from '../../../types/restaurant/RestaurantReservationTypes';

const pad = (value: number) => String(value).padStart(2, '0');

export const toLocalDateTime = (date: string, time: string) =>
  `${date}T${time.length === 5 ? `${time}:00` : time}`;

export const defaultReservationForm = (): ReservationFormState => {
  const nextHour = new Date();
  nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
  return {
    customerMode: 'GUEST',
    customerId: '',
    guestName: '',
    guestPhone: '',
    guestEmail: '',
    partySize: 2,
    date: `${nextHour.getFullYear()}-${pad(nextHour.getMonth() + 1)}-${pad(nextHour.getDate())}`,
    time: `${pad(nextHour.getHours())}:${pad(nextHour.getMinutes())}`,
    durationMinutes: 90,
    tableIds: [],
    notes: '',
  };
};

export const reservationToForm = (reservation: Reservation): ReservationFormState => {
  const [date, rawTime = ''] = reservation.startsAt.split('T');
  const duration = Math.round(
    (new Date(reservation.endsAt).getTime() - new Date(reservation.startsAt).getTime()) / 60000,
  );
  return {
    id: reservation.id,
    customerMode: reservation.customerId ? 'REGISTERED' : 'GUEST',
    customerId: reservation.customerId ?? '',
    guestName: reservation.guestName ?? '',
    guestPhone: reservation.guestPhone ?? '',
    guestEmail: reservation.guestEmail ?? '',
    partySize: reservation.partySize,
    date,
    time: rawTime.slice(0, 5),
    durationMinutes: duration,
    tableIds: reservation.tables.map((table) => table.id),
    notes: reservation.notes ?? '',
  };
};

export const reservationPayload = (form: ReservationFormState): ReservationRequest => ({
  customerId: form.customerMode === 'REGISTERED' ? Number(form.customerId) : null,
  guestName: form.customerMode === 'GUEST' ? form.guestName.trim() || null : null,
  guestPhone: form.customerMode === 'GUEST' ? form.guestPhone.trim() || null : null,
  guestEmail: form.customerMode === 'GUEST' ? form.guestEmail.trim() || null : null,
  partySize: Number(form.partySize),
  startsAt: toLocalDateTime(form.date, form.time),
  durationMinutes: Number(form.durationMinutes),
  tableIds: form.tableIds,
  notes: form.notes.trim() || null,
});

export const validateReservation = (
  form: ReservationFormState,
  capacity: number,
  allowOverbooking: boolean,
): ReservationFieldErrors => {
  const errors: ReservationFieldErrors = {};
  if (form.customerMode === 'REGISTERED' && !form.customerId) errors.customerId = 'Seleccione un cliente.';
  if (form.customerMode === 'GUEST' && !form.guestName.trim()) errors.guestName = 'Ingrese el nombre del invitado.';
  if (form.partySize < 1 || form.partySize > 1000) errors.partySize = 'Debe estar entre 1 y 1000.';
  if (form.durationMinutes < 15 || form.durationMinutes > 1440) errors.durationMinutes = 'Debe estar entre 15 y 1440 minutos.';
  if (!form.date) errors.date = 'Seleccione una fecha.';
  if (!form.time) errors.time = 'Seleccione una hora.';
  if (!form.tableIds.length) errors.tableIds = 'Seleccione al menos una mesa.';
  if (!allowOverbooking && form.tableIds.length && capacity < form.partySize) {
    errors.tableIds = `La capacidad seleccionada (${capacity}) no alcanza para ${form.partySize} personas.`;
  }
  return errors;
};

export const isArrivalWindowOpen = (reservation: Reservation, toleranceMinutes: number) => {
  const now = Date.now();
  const from = new Date(reservation.startsAt).getTime() - toleranceMinutes * 60000;
  const to = new Date(reservation.endsAt).getTime() + toleranceMinutes * 60000;
  return now >= from && now <= to;
};

